import {
  AssemblePayloadRequest,
  CoreApplicationObjectType,
  DeviceControlTransactionProps,
  DeviceTransactionStatus,
  DownlinkAction,
  MapLike,
  NetworkKey,
  ValidBusinessObject,
} from '@iotv/iotv-v3-types'
import { v4 } from 'uuid'
import { ValidModelObject } from '../../classDependentTypes'
import { DeviceProps } from '../../ModelClassInterfaces/DeviceType'
import { getTTLSeconds } from '../../util'
import { DeviceControlTransaction } from './DeviceControlTransaction'

/** the rolling or sequential programme sk that produced the individual device transactions, plus timestamp (together a unique id of when was sent )
 * This is propogated through
 * device-controller, device-state, device-control-logs in s3, the ui (via @iotv/lfc-devices)
 * It is critical to identiying and matching programme configurations.
 */
export type BroadcastData = {
  /** an optional key to tie individual requests to a single coordingating programme */
  broadcastScheduleSk: string
  broadcastTimestamp: number
}

const debugOverride = process.env.DEBUG_OVERRIDE === 'true'
const debug = (!process.env.AWS_SESSION_TOKEN && false) || debugOverride

export type GenerateDeviceControlTransactionRequest = {
  broadcastData?: BroadcastData
  messageType?: number
  device: ValidModelObject<'Device'>
  desiredPayload: MapLike<any>
  requestInitiator: ValidBusinessObject
}

export const generateDeviceControlTransaction = ({
  broadcastData,
  device,
  desiredPayload,
  requestInitiator,
  messageType,
}: GenerateDeviceControlTransactionRequest) => {
  const MessageType = device.deviceType ?? messageType
  const { appID, deviceId, networkKey } = device as DeviceProps & CoreApplicationObjectType
  const params: AssemblePayloadRequest = {
    ApplicationId: appID,
    device,
    RequestId: v4(),
    MessageType,
    desiredPayload,
    downlinkAction: DownlinkAction.PUSH,
    networkKey: networkKey as NetworkKey,
  }

  const { broadcastScheduleSk, broadcastTimestamp } = broadcastData ?? {}
  const now = Date.now()
  const timestamp = broadcastTimestamp ?? now
  const ttl = getTTLSeconds(1) // 1 day from now
  // PUT and INSERT will fail if an empty string is used in an index
  const dKeys = deviceId && deviceId !== '' ? deviceId : undefined

  const props: DeviceControlTransactionProps = {
    devicePk: device.sk,
    initialRequest: params,
    requestInitiator,
    status: DeviceTransactionStatus.USER_REQUESTED,
    timestamp,
    ttl,
    dpk: dKeys,
    dsk: dKeys,
    broadcastScheduleSk,
  }

  /** 230916 changed transaction id to be begins_with sortable on timestamp in ms, from uuid v4, appended with device id to prevent per millisecond duplicates (!!) */
  const transactionId = `${now}_${device.id}`
  const sk = `DeviceControlTransaction:${transactionId}`
  const id = transactionId

  const deviceControlTransaction = new DeviceControlTransaction(props)

  const transactionName = `${requestInitiator.name ?? requestInitiator.id} DCT for ${device.eui ?? device.id} `

  deviceControlTransaction.name = transactionName

  deviceControlTransaction.sk = sk
  deviceControlTransaction.id = id

  deviceControlTransaction.transactionStages.push({
    status: DeviceTransactionStatus.USER_REQUESTED,
    timestampsMs: now,
    correlationId: undefined,
    responseObject: undefined,
  })

  debug && console.log('JSON for test', JSON.stringify(deviceControlTransaction, null, 1))

  return deviceControlTransaction
}
